import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "@/lib/api";
import { encryptForConversation } from "@/lib/e2ee";
import type { LocationPayload } from "@/types";
import { toast } from "sonner";

const SEND_THROTTLE_MS = 20_000;

interface ActiveShare {
  conversationId: string;
  shareId: string;
  expiresAt: string;
}

interface LiveLocationContextValue {
  activeShare: ActiveShare | null;
  /** serverCurrentEpoch is captured once, at the moment sharing starts - if a
   * rotation happens WHILE a share is already active, updates sent for the
   * remainder of that share keep using the epoch from when it started until
   * the share is stopped and restarted. A real, known gap, but a much
   * narrower one than the core vulnerability this whole epoch system exists
   * to close: it requires a member to be removed during someone's already-
   * active location share specifically, not simply being a member at all. */
  startSharing: (conversationId: string, durationSeconds: number, serverCurrentEpoch: number) => Promise<void>;
  stopSharing: () => Promise<void>;
}

const LiveLocationContext = createContext<LiveLocationContextValue | null>(null);

export function LiveLocationProvider({ children }: { children: ReactNode }) {
  const [activeShare, setActiveShare] = useState<ActiveShare | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const lastSentAtRef = useRef(0);
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeShareRef = useRef<ActiveShare | null>(null);
  activeShareRef.current = activeShare;

  const cleanupWatch = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  }, []);

  const stopSharing = useCallback(async () => {
    const share = activeShareRef.current;
    cleanupWatch();
    setActiveShare(null);
    if (share) {
      try {
        await api.post(`/api/conversations/live-location/${share.shareId}/stop`);
      } catch {
        // Best-effort - the share will also expire server-side on its own,
        // so a failed stop call here doesn't leave anything stuck forever.
      }
    }
  }, [cleanupWatch]);

  const startSharing = useCallback(
    async (conversationId: string, durationSeconds: number, serverCurrentEpoch: number) => {
      if (activeShareRef.current) await stopSharing();

      if (!navigator.geolocation) {
        toast.error("Location isn't available in this browser");
        return;
      }

      let share: { id: string; expires_at: string };
      try {
        share = await api.post(`/api/conversations/${conversationId}/live-location/start`, { durationSeconds });
      } catch {
        toast.error("Couldn't start sharing your location");
        return;
      }

      const nextShare: ActiveShare = { conversationId, shareId: share.id, expiresAt: share.expires_at };
      setActiveShare(nextShare);
      activeShareRef.current = nextShare;
      lastSentAtRef.current = 0;

      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          const now = Date.now();
          if (now - lastSentAtRef.current < SEND_THROTTLE_MS) return;
          lastSentAtRef.current = now;

          void (async () => {
            const payload: LocationPayload = { type: "location", lat: pos.coords.latitude, lng: pos.coords.longitude };
            try {
              const { content, nonce, keyEpoch } = await encryptForConversation(conversationId, serverCurrentEpoch, JSON.stringify(payload));
              await api.post(`/api/conversations/${conversationId}/messages`, {
                content,
                nonce,
                keyEpoch,
                messageType: "location",
                liveLocationShareId: share.id
              });
            } catch {
              // A single missed update isn't worth surfacing - the next
              // watchPosition tick will just try again.
            }
          })();
        },
        () => {
          toast.error("Lost access to your location - sharing has stopped");
          void stopSharing();
        },
        { enableHighAccuracy: false, maximumAge: 10_000 }
      );

      const msUntilExpiry = new Date(share.expires_at).getTime() - Date.now();
      expiryTimerRef.current = setTimeout(() => void stopSharing(), Math.max(0, msUntilExpiry));
    },
    [stopSharing]
  );

  useEffect(() => cleanupWatch, [cleanupWatch]);

  return (
    <LiveLocationContext.Provider value={{ activeShare, startSharing, stopSharing }}>
      {children}
    </LiveLocationContext.Provider>
  );
}

export function useLiveLocation() {
  const ctx = useContext(LiveLocationContext);
  if (!ctx) throw new Error("useLiveLocation must be used within LiveLocationProvider");
  return ctx;
}
